/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/forbid-prop-types */
/* eslint-disable no-restricted-syntax */
import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import PropTypes from 'prop-types';
import BigNumber from 'bignumber.js';
import ModalBodyLayout from '../../../shared/ModalBodyLayout';
import { RATES_QUERY_KEY, USER_PREFERENCES } from '../../../../utilities/sharedQueries';
import { formatNumber } from '../../../../utilities/formatHelpers';
import { currencies, exchange } from '../../../../utilities/constants';
import { keysToUpperCase, toLowerCaseArray } from '../../../../utilities/helpers';
import { useUserWallets } from '../../hooks';
import { USER_CREATE_WALLET } from '../../mutations';
import { showAlert } from '../../../../utilities/notifications';
import { WalletPopupItemPlaceholder } from '../../../../components/Placeholders';
import { WALLETS_ALL } from '../../queries';
import WalletWithCheckbox from '../../../../components/WalletWithCheckbox';
import {
  cacheHiddenUserCurrencyList,
  getCachedHiddenUserCurrencyList,
} from '../../../../utilities/caching';
import { FLOWS, sendStats } from '../../../../services/mixpanel';
import { MXP_ACCOUNT_ADD } from '../../mixpanel-events';

const SelectCurrency = ({ onCloseModal, allowedCurrencies, exitedWallets }) => {
  const queryClient = useQueryClient();

  const { data: userPreferences } = useQuery(USER_PREFERENCES);
  const [userWallets, refetchUserWallets, isLoading] = useUserWallets();
  const [walletsToCreate, updateWalletsToCreate] = useState([...exitedWallets]);
  const { data: userPref } = useQuery(USER_PREFERENCES);
  const { data: rates } = useQuery(RATES_QUERY_KEY);
  const displayCurrency = userPref?.displayCurrency || 'EUR';
  const _destination = displayCurrency.toUpperCase();

  const getRateForCoin = useCallback(
    (_source) => {
      let _rate = 0;
      if (rates) {
        if (_source === _destination) {
          _rate = 1;
        } else if (rates[`${_source}${_destination}`]) {
          _rate = new BigNumber(rates[`${_source}${_destination}`].ask);
        } else if (rates[`${_destination}${_source}`]) {
          _rate = new BigNumber(1).dividedBy(rates[`${_destination}${_source}`].ask);
        }
      }

      return formatNumber(_rate, currencies[_destination].decimal);
    },
    [_destination, rates]
  );

  const { mutate: createWallets, isLoading: isCreatingWallets } = useMutation(USER_CREATE_WALLET, {
    onSettled: (data, error) => {
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      } else {
        refetchUserWallets();
        onCloseModal();
        toLowerCaseArray(walletsToCreate).forEach((coin) => {
          if (data[coin]) {
            const { error: walletError } = data[coin];
            if (walletError) {
              if (walletError.error) {
                showAlert({
                  message: walletError.error.message,
                  type: 'danger',
                });
              } else {
                showAlert({ message: walletError, type: 'danger' });
              }
            }
          }
        });
        queryClient.resetQueries(WALLETS_ALL);
      }
    },
  });

  const { t: translate } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const handleSearchInput = (event) => {
    setSearchValue(event.target.value);
  };

  const coins = Object.keys(keysToUpperCase(allowedCurrencies));

  const balancesData = useMemo(() => {
    if (userPreferences && userWallets) {
      const result = {};
      const userWalletsUpperCase = keysToUpperCase(userWallets);

      // Override the case of missing rate field.
      Object.keys(userWalletsUpperCase).forEach((coin) => {
        if (!userWalletsUpperCase[coin].rate) {
          userWalletsUpperCase[coin].rate = getRateForCoin(coin.toUpperCase());
        }
      });
      coins.forEach((coin) => {
        const currency = currencies[coin];
        const wallet = userWalletsUpperCase[coin.toUpperCase()];
        const balance = formatNumber(
          wallet?.available_balance * currency.conversion,
          currency.decimal
        );
        result[coin] = formatNumber(wallet?.rate * balance, exchange.FIAT_VALUE);
      });

      return result;
    }

    return null;
  }, [userPreferences, userWallets, coins, getRateForCoin]);

  const sortedCoins = useMemo(
    () =>
      Object.entries(balancesData).sort(
        (a, b) => parseFloat(b[1]) - parseFloat(a[1]) || a[0].localeCompare(b[0])
      ),
    [balancesData]
  );

  const filteredCoins = useMemo(
    () =>
      sortedCoins.filter(
        (coin) =>
          coin[0].search(searchValue.toUpperCase()) >= 0 ||
          currencies[coin]?.fullName?.toUpperCase().search(searchValue.toUpperCase()) >= 0
      ),
    [searchValue, sortedCoins]
  );

  const handleSelect = (e, coin) => {
    if (e.target.checked) {
      updateWalletsToCreate((prev) => [...prev, coin]);
    } else {
      updateWalletsToCreate((oldValue) => oldValue.filter((item) => item !== coin));
    }
  };

  const renderHeader = () => (
    <div>
      <p className="text-shadow text-f6 my-1">{translate('select_currenct_accounts_sublabel')}</p>
      <div className="my-3 ">
        <div className="rounded bg-light p-0 popup-search-input d-flex flex-row flex-row-reverse position-relative select-currency-header-input-container">
          <input
            type="text"
            value={searchValue}
            onChange={(e) => handleSearchInput(e)}
            className="form-control border-1 border-transparent bg-light shadow-none w-100 ps-5 d-flex"
            placeholder={translate('select_account_input_placeholder')}
          />
          <span className="position-absolute start-0 top-0 px-3 py-3 icon icon-search-other h-100 d-flex align-items-center justify-content-center text-shadow" />
          <span
            style={{ height: '2.5rem', width: '2rem' }}
            className={`text-dark text-bold icon-close-other position-absolute d-flex align-items-center justify-content-center top-50 end-0 translate-middle-y me-2 cursor-pointer ${
              searchValue === '' ? 'd-none' : ''
            }`}
            tabIndex={-1}
            role="button"
            onClick={() => {
              setSearchValue('');
            }}
            onKeyDown={() => {}}>
            {' '}
          </span>
        </div>
      </div>
    </div>
  );

  const renderFooter = () => (
    <button
      type="button"
      className="btn btn-primary py-3 d-flex justify-content-center align-items-center"
      loading={isCreatingWallets ? 'true' : 'false'}
      disabled={isCreatingWallets}
      onClick={(e) => {
        sendStats(MXP_ACCOUNT_ADD, { wallets: walletsToCreate }, FLOWS.ACCOUNTS);
        const hiddenByUser = getCachedHiddenUserCurrencyList();
        const checkedOnBackend = [...hiddenByUser, ...exitedWallets];
        const walletsToAdd = walletsToCreate.filter((x) => !checkedOnBackend.includes(x));
        const hidenForFrontend = checkedOnBackend.filter((x) => !walletsToCreate.includes(x));
        e.target.closest('html').classList.remove('overflow-hidden');
        if (walletsToAdd.length > 0) {
          createWallets({
            wallets: toLowerCaseArray(walletsToAdd),
          });
          cacheHiddenUserCurrencyList(hidenForFrontend);
        } else {
          cacheHiddenUserCurrencyList(hidenForFrontend);
          refetchUserWallets();
          onCloseModal();
        }
      }}>
      {translate('confirm_button_label')}
    </button>
  );

  const renderWalletItem = (coin) => (
    <WalletWithCheckbox
      key={coin}
      walletIcon={coin || `BTC`}
      isChecked={walletsToCreate.indexOf(coin) >= 0}
      handleSelect={(e) => handleSelect(e, coin)}>
      <b className="upper-case">{coin}</b>
      {balancesData && (
        <p className="text-shadow text-f6 custom-subtext">
          {`${balancesData[coin]} ${userPreferences.displayCurrency.toUpperCase()}`}
        </p>
      )}
    </WalletWithCheckbox>
  );

  return (
    <ModalBodyLayout
      withBack={false}
      title={translate('manage_accounts_label')}
      renderFooter={renderFooter}
      renderHeader={renderHeader}>
      {isLoading ? (
        <div className="wallet-list">
          <WalletPopupItemPlaceholder />
          <WalletPopupItemPlaceholder />
          <WalletPopupItemPlaceholder />
          <WalletPopupItemPlaceholder />
        </div>
      ) : (
        filteredCoins.map((coin) => renderWalletItem(coin[0]))
      )}
    </ModalBodyLayout>
  );
};

export default SelectCurrency;

SelectCurrency.propTypes = {
  onCloseModal: PropTypes.func.isRequired,
  allowedCurrencies: PropTypes.object.isRequired,
  exitedWallets: PropTypes.arrayOf(PropTypes.string).isRequired,
};
